import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { format, isToday } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Download, ExternalLink, Clock, Phone, Trash2 } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { EditNotesDialog } from "@/components/EditNotesDialog";
import { useToast } from "@/hooks/use-toast";
import { useUserRole } from "@/hooks/useUserRole";
import { AppSidebar } from "@/components/AppSidebar";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface FormSubmission {
  id: string;
  email_form_id: string;
  email: string;
  name: string | null;
  phone: string | null;
  city: string | null;
  state: string | null;
  company: string | null;
  contact_id: number | null;
  processed: boolean;
  error_message?: string | null;
  created_at: string;
  contacted: boolean;
  contact_status: 'pending' | 'contacted' | 'no_answer' | 'scheduled' | 'converted' | 'opt_out';
  notes: string | null;
  contacted_at: string | null;
  contacted_by: string | null;
  email_forms: {
    name: string;
  } | null;
  contacts: {
    company: string;
  } | null;
}

const STATUS_LABELS = {
  pending: { label: "Pendente", color: "bg-yellow-100 text-yellow-800 border-yellow-300" },
  contacted: { label: "Contactado", color: "bg-green-100 text-green-800 border-green-300" },
  no_answer: { label: "Sem Resposta", color: "bg-red-100 text-red-800 border-red-300" },
  scheduled: { label: "Agendado", color: "bg-blue-100 text-blue-800 border-blue-300" },
  converted: { label: "Convertido", color: "bg-purple-100 text-purple-800 border-purple-300" },
  opt_out: { label: "Não Contactar", color: "bg-gray-100 text-gray-800 border-gray-300" },
};

export default function FormSubmissions() {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedForm, setSelectedForm] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [showOptOuts, setShowOptOuts] = useState<boolean>(false);
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { isAdmin, isEditor, isRsvp, isCliente } = useUserRole();
  const canManage = isAdmin || isEditor || isRsvp;

  const { data: forms = [] } = useQuery({
    queryKey: ["email-forms-list"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("email_forms")
        .select("id, name")
        .eq("is_active", true);

      if (error) {
        console.error("[FORMS] Erro ao carregar formulários:", error);
        toast({
          title: "Erro ao carregar formulários",
          description: "Não foi possível carregar a lista de formulários.",
          variant: "destructive",
        });
        throw error;
      }
      return data;
    },
    retry: false,
  });

  const { data: submissions = [], isLoading } = useQuery({
    queryKey: ["form-submissions", selectedForm],
    queryFn: async () => {
      let query = supabase
        .from("google_form_submissions")
        .select(`
          *,
          email_forms (name),
          contacts (company)
        `)
        .order("created_at", { ascending: false });

      if (selectedForm !== "all") {
        query = query.eq("email_form_id", selectedForm);
      }

      const { data, error } = await query;
      if (error) {
        console.error("[SUBMISSIONS] Erro ao carregar leads:", error);
        toast({
          title: "Erro ao carregar leads",
          description: "Não foi possível carregar a lista de leads.",
          variant: "destructive",
        });
        throw error;
      }
      return data as FormSubmission[];
    },
    retry: false,
  });

  const filteredSubmissions = submissions.filter((sub) => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = (
      sub.email.toLowerCase().includes(searchLower) ||
      sub.name?.toLowerCase().includes(searchLower) ||
      sub.phone?.toLowerCase().includes(searchLower) ||
      sub.email_forms?.name?.toLowerCase().includes(searchLower)
    );
    
    const matchesStatus = statusFilter === "all" || sub.contact_status === statusFilter;
    
    // Esconder opt-outs por padrão, a menos que o usuário peça explicitamente para ver
    const shouldShow = showOptOuts || sub.contact_status !== 'opt_out';
    
    return matchesSearch && matchesStatus && shouldShow;
  });

  const handleStatusChange = async (submissionId: string, newStatus: string) => {
    try {
      // Buscar o submission atual para verificar se já foi contactado
      const currentSubmission = submissions.find(s => s.id === submissionId);
      
      const updateData: any = { contact_status: newStatus };
      
      // Se está mudando para "contacted" e ainda não foi contactado, registrar histórico
      if (newStatus === 'contacted' && currentSubmission && !currentSubmission.contacted) {
        const { data: { user } } = await supabase.auth.getUser();
        updateData.contacted = true;
        updateData.contacted_at = new Date().toISOString();
        updateData.contacted_by = user?.id;
      }
      
      const { error } = await supabase
        .from("google_form_submissions")
        .update(updateData)
        .eq("id", submissionId);

      if (error) throw error;

      toast({
        title: "Status atualizado",
        description: "O status do lead foi alterado com sucesso.",
      });
      queryClient.invalidateQueries({ queryKey: ["form-submissions"] });
    } catch (error) {
      console.error("Error updating status:", error);
      toast({
        title: "Erro ao atualizar",
        description: "Não foi possível alterar o status.",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (submissionId: string, submissionEmail: string) => {
    try {
      const { error } = await supabase
        .from("google_form_submissions")
        .delete()
        .eq("id", submissionId);

      if (error) throw error;

      toast({
        title: "Lead deletado",
        description: `O lead ${submissionEmail} foi permanentemente removido.`,
      });
      queryClient.invalidateQueries({ queryKey: ["form-submissions"] });
    } catch (error) {
      console.error("Error deleting submission:", error);
      toast({
        title: "Erro ao deletar",
        description: "Não foi possível remover o lead.",
        variant: "destructive",
      });
    }
  };

  const exportToCSV = () => {
    const headers = ["Data", "Formulário", "Empresa", "Email", "Nome", "Telefone", "Status Contato", "Contactado", "Data Contato", "Observações"];
    const rows = filteredSubmissions.map((sub) => [
      format(new Date(sub.created_at), "dd/MM/yyyy HH:mm", { locale: ptBR }),
      sub.email_forms?.name || "N/A",
      sub.contacts?.company || sub.company || "Não informado",
      sub.email,
      sub.name || "",
      sub.phone || "",
      STATUS_LABELS[sub.contact_status].label,
      sub.contacted ? "Sim" : "Não",
      sub.contacted_at ? format(new Date(sub.contacted_at), "dd/MM/yyyy HH:mm", { locale: ptBR }) : "",
      sub.notes || "",
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `inscricoes_${format(new Date(), "yyyy-MM-dd")}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const pendingLeads = filteredSubmissions.filter((s) => !s.contacted);
  const contactedToday = filteredSubmissions.filter((s) => 
    s.contacted_at && isToday(new Date(s.contacted_at))
  );
  const converted = filteredSubmissions.filter((s) => s.contact_status === 'converted');
  const optOuts = submissions.filter((s) => s.contact_status === 'opt_out');

  const stats = {
    total: filteredSubmissions.length,
    pending: pendingLeads.length,
    contactedToday: contactedToday.length,
    conversionRate: filteredSubmissions.length > 0 
      ? ((converted.length / filteredSubmissions.length) * 100).toFixed(1) 
      : "0.0",
    optOuts: optOuts.length,
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full">
        <AppSidebar onClearData={() => {}} />
        <main className="flex-1">
          <div className="border-b bg-background">
            <div className="flex h-14 items-center gap-4 px-6">
              <SidebarTrigger />
              <h1 className="text-xl font-semibold">Gestão de Leads</h1>
            </div>
          </div>
          <div className="container mx-auto py-8 px-4">
            <div className="mb-8">
              <p className="text-muted-foreground mt-2">
                Gerencie o processo de contato comercial com os leads dos formulários
              </p>
            </div>

      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Total de Leads</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
            <p className="text-xs text-muted-foreground mt-1">Leads recebidos</p>
          </CardContent>
        </Card>
        <Card className={stats.pending > 0 ? "border-yellow-500" : ""}>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Aguardando Contato</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-600">{stats.pending}</div>
            <p className="text-xs text-muted-foreground mt-1">Precisam ser contactados</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Contactados Hoje</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{stats.contactedToday}</div>
            <p className="text-xs text-muted-foreground mt-1">Contatos realizados hoje</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Taxa de Conversão</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-600">{stats.conversionRate}%</div>
            <p className="text-xs text-muted-foreground mt-1">Leads convertidos</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Opt-Outs</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-600">{stats.optOuts}</div>
            <p className="text-xs text-muted-foreground mt-1">Não querem contato</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <Label htmlFor="search">Buscar</Label>
              <Input
                id="search"
                placeholder="Pesquisar por email, nome, telefone ou formulário..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="w-full md:w-48">
              <Label htmlFor="form-filter">Formulário</Label>
              <Select value={selectedForm} onValueChange={setSelectedForm}>
                <SelectTrigger id="form-filter">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {forms.map((form) => (
                    <SelectItem key={form.id} value={form.id}>
                      {form.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-full md:w-48">
              <Label htmlFor="status-filter">Status do Contato</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger id="status-filter">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os status</SelectItem>
                  <SelectItem value="pending">Pendente</SelectItem>
                  <SelectItem value="contacted">Contactado</SelectItem>
                  <SelectItem value="no_answer">Sem Resposta</SelectItem>
                  <SelectItem value="scheduled">Agendado</SelectItem>
                  <SelectItem value="converted">Convertido</SelectItem>
                  <SelectItem value="opt_out">Não Contactar</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end gap-2">
              <Button 
                onClick={() => setShowOptOuts(!showOptOuts)} 
                variant={showOptOuts ? "default" : "outline"}
              >
                {showOptOuts ? "Ocultar" : "Ver"} Opt-Outs ({stats.optOuts})
              </Button>
              <Button onClick={exportToCSV} variant="outline">
                <Download className="h-4 w-4 mr-2" />
                Exportar CSV
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filteredSubmissions.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              Nenhuma inscrição encontrada
            </div>
          ) : (
            <TooltipProvider>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Data</TableHead>
                      <TableHead>Formulário</TableHead>
                      <TableHead>Empresa</TableHead>
                      <TableHead>Lead</TableHead>
                      <TableHead>Contato</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Observações</TableHead>
                      <TableHead>Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredSubmissions.map((submission) => (
                      <TableRow 
                        key={submission.id}
                        className={!submission.contacted ? "bg-yellow-50/50" : ""}
                      >
                        <TableCell className="whitespace-nowrap">
                          {format(new Date(submission.created_at), "dd/MM/yyyy", { locale: ptBR })}
                          <div className="text-xs text-muted-foreground">
                            {format(new Date(submission.created_at), "HH:mm", { locale: ptBR })}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs">
                            {submission.email_forms?.name || "Desconhecido"}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            {submission.contacts?.company || submission.company ? (
                              <span className="font-medium">
                                {submission.contacts?.company || submission.company}
                              </span>
                            ) : (
                              <span className="text-muted-foreground">Não informado</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            <div className="font-medium">{submission.name || "Sem nome"}</div>
                            <div className="text-xs text-muted-foreground">
                              {submission.email}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {submission.phone ? (
                              <a 
                                href={`tel:${submission.phone}`}
                                className="flex items-center gap-1 text-sm hover:text-primary"
                              >
                                <Phone className="h-3 w-3" />
                                {submission.phone}
                              </a>
                            ) : (
                              <span className="text-muted-foreground text-sm">Sem telefone</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div>
                                  <Select
                                    value={submission.contact_status}
                                    onValueChange={(value) => handleStatusChange(submission.id, value)}
                                    disabled={!canManage}
                                  >
                                    <SelectTrigger className="w-40">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="pending">🟡 Pendente</SelectItem>
                                      <SelectItem value="contacted">🟢 Contactado</SelectItem>
                                      <SelectItem value="no_answer">🔴 Sem Resposta</SelectItem>
                                      <SelectItem value="scheduled">🔵 Agendado</SelectItem>
                                      <SelectItem value="converted">✅ Convertido</SelectItem>
                                      <SelectItem value="opt_out">🚫 Não Contactar</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                              </TooltipTrigger>
                              {!canManage && (
                                <TooltipContent>
                                  <p>Somente administradores e editores podem alterar o status</p>
                                </TooltipContent>
                              )}
                            </Tooltip>
                            {submission.contacted && submission.contacted_at && (
                              <Tooltip>
                                <TooltipTrigger>
                                  <Badge variant="secondary" className="text-xs flex items-center gap-1">
                                    <Clock className="h-3 w-3" />
                                    Histórico
                                  </Badge>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p className="text-sm">Primeiro contato em:</p>
                                  <p className="text-sm font-medium">
                                    {format(new Date(submission.contacted_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                                  </p>
                                </TooltipContent>
                              </Tooltip>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {submission.notes ? (
                              <Tooltip>
                                <TooltipTrigger>
                                  <div className="max-w-xs truncate text-sm">
                                    {submission.notes}
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent className="max-w-md">
                                  <p className="text-sm whitespace-pre-wrap">{submission.notes}</p>
                                </TooltipContent>
                              </Tooltip>
                            ) : (
                              <span className="text-muted-foreground text-sm">-</span>
                            )}
                            {canManage && (
                              <EditNotesDialog
                                submissionId={submission.id}
                                currentNotes={submission.notes}
                                onNotesUpdated={() => queryClient.invalidateQueries({ queryKey: ["form-submissions"] })}
                              />
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {submission.contact_id && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <a
                                    href="/contacts"
                                    className="text-primary hover:underline inline-flex items-center gap-1 text-sm"
                                  >
                                    <ExternalLink className="h-3 w-3" />
                                    Ver Contato
                                  </a>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Ver contato vinculado</p>
                                </TooltipContent>
                              </Tooltip>
                            )}
                            {isAdmin && (
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-destructive hover:text-destructive">
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Confirmar Deleção Permanente</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      Tem certeza que deseja deletar permanentemente o lead <strong>{submission.email}</strong>?
                                      <br /><br />
                                      Esta ação <strong>não pode ser desfeita</strong> e todos os dados serão perdidos.
                                      <br /><br />
                                      <span className="text-yellow-600 font-medium">
                                        ⚠️ Recomendamos usar o status "🚫 Não Contactar" ao invés de deletar.
                                      </span>
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                    <AlertDialogAction
                                      onClick={() => handleDelete(submission.id, submission.email)}
                                      className="bg-destructive hover:bg-destructive/90"
                                    >
                                      Deletar Permanentemente
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </TooltipProvider>
          )}
        </CardContent>
      </Card>
          </div>
        </main>
      </div>
    </SidebarProvider>
  );
}
